/**
 * ProjectList Component
 * Displays projects table with TGDataTable framework
 */

import React from 'react';
import { Button, Tag, Tooltip, Spin } from 'antd';
import {
  DashboardOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  SettingOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { TGDataTable } from '../../../components/common';
import { getTrustScoreColor } from '../api/trustgridApi';
import { colors } from '../../../theme/designTokens';

/**
 * Status indicator renderer
 */
function StatusIndicator({ record }) {
  const hasRecentScan = record.trust_score !== undefined && record.trust_score !== null;
  if (!hasRecentScan) {
    return <span style={{ color: '#ed6c02', fontSize: 18 }}>⚠</span>;
  }
  return record.trust_score >= 70 ? (
    <span style={{ color: '#2e7d32', fontSize: 18 }}>✓</span>
  ) : (
    <span style={{ color: '#d32f2f', fontSize: 18 }}>⚠</span>
  );
}

/**
 * Trust Score renderer with color coding
 */
function TrustScoreDisplay({ score }) {
  if (!score && score !== 0) return '—';

  const color = getTrustScoreColor(score);
  const tagColor = color === 'success' ? 'green' : color === 'warning' ? 'orange' : 'red';

  return <Tag color={tagColor}>{score}</Tag>;
}

/**
 * ProjectList Component
 */
function ProjectList({
  data,
  loading,
  onRefresh,
  onOpenDashboard,
  onManageControls,
  onRunScan,
  onViewReports,
  onDelete,
  runningScans = {},
  toolbar,
  externalFilterCount,
  onClearExternalFilters,
}) {
  const columns = [
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 80,
      align: 'center',
      render: (_, record) => <StatusIndicator record={record} />,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Healthy', value: 'healthy' },
        { label: 'Warning', value: 'warning' },
        { label: 'No Scan', value: 'no_scan' },
      ],
    },
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      render: (desc) => desc || '—',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'updated_at',
      title: 'Last Scan',
      dataIndex: 'updated_at',
      width: 150,
      render: (date) => (date ? new Date(date).toLocaleDateString() : 'Never'),
      sortable: true,
    },
    {
      key: 'trust_score',
      title: 'Trust Score',
      dataIndex: 'trust_score',
      width: 120,
      align: 'center',
      render: (score) => <TrustScoreDisplay score={score} />,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Open Dashboard">
            <Button
              size="small"
              icon={<DashboardOutlined />}
              onClick={() => onOpenDashboard(record.id)}
            />
          </Tooltip>
          <Tooltip title="Manage Controls">
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => onManageControls(record)}
            />
          </Tooltip>
          <Tooltip title="Run Scan">
            <Button
              size="small"
              icon={runningScans[record.id] ? <Spin size="small" /> : <PlayCircleOutlined />}
              onClick={() => onRunScan(record.id)}
              disabled={runningScans[record.id]}
            />
          </Tooltip>
          <Tooltip title="View Reports">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => onViewReports(record.id)}
            />
          </Tooltip>
          <Tooltip title="Delete Project">
            <Button
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
              danger
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <TGDataTable
      data={data}
      columns={columns}
      loading={loading}
      rowKey="id"
      pagination="client"
      pageSize={10}
      showExport
      showColumnConfig
      showFilters
      exportFilename="trustgrid-projects"
      presetKey="trustgrid.projects"
      onRefresh={onRefresh}
      toolbar={toolbar}
      externalFilterCount={externalFilterCount}
      onClearExternalFilters={onClearExternalFilters}
    />
  );
}

export default ProjectList;
